import { Alert, AlertTitle, Box, Button, Chip, LinearProgress, Stack, Typography } from '@mui/material';

import type { FeedTemplateSwitchStage, FeedTemplateTarget } from '../../@types/api';
import { dayjs } from '../../config/dayjs';
import { DATE_TIME_FORMAT } from '../../constants';
import { useModal } from '../../contexts/ModalContext';
import { useFeedTemplateSwitch } from '../../hooks/useFeedTemplateSwitch';
import { ReleaseTemplateSwitch } from './ReleaseTemplateSwitch';
import ConfirmationModal from '../modals/ConfirmationModal';

const STATUS_LABELS: Record<FeedTemplateSwitchStage['status'], string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  succeeded: 'Succeeded',
  failed: 'Failed',
  skipped: 'Skipped',
};

const STATUS_COLORS = {
  pending: 'default',
  in_progress: 'info',
  succeeded: 'success',
  failed: 'error',
  skipped: 'default',
} as const;

const OPERATION_LABELS = {
  running: 'Switch in progress',
  succeeded: 'Switch execution completed',
  failed: 'Switch failed',
  interrupted: 'Switch interrupted',
} as const;

const ORIGIN_LABELS = {
  manual: 'Manual BO switch',
  automatic: 'Automatic provider signal',
  legacy: 'Legacy activation',
} as const;

const targetLabel = (target: FeedTemplateTarget | null) => {
  if (target === null) return 'Unconfirmed';
  return target === 'main' ? 'Main' : 'Backup';
};

const feedRunningLabel = (value: boolean | null) => {
  if (value === null) return 'Unconfirmed';
  return value ? 'On' : 'Off';
};

const styles = {
  title: { fontWeight: 600 },
  wrap: { overflowWrap: 'anywhere' },
  activeBadge: { ml: 1 },
  stageList: { p: 0, m: 0, listStyle: 'none' },
  refresh: { alignSelf: 'flex-start' },
  selector: { flexDirection: 'row', p: 0.5, gap: 0.5, borderRadius: 6, bgcolor: 'action.hover' },
  templateOption: {
    minHeight: 44,
    borderRadius: 6,
    textTransform: 'none',
  },
  stage: { p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 },
  row: {
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    gap: 1,
    alignItems: { xs: 'flex-start', md: 'center' },
  },
};

export const FeedTemplateSwitchView = ({ controls }: { controls: ReturnType<typeof useFeedTemplateSwitch> }) => {
  const { openModal, closeModal } = useModal();
  const { operation, feedRunning, activeTemplate, disabled, statusUnavailable, startError, isLoading, start, refresh } =
    controls;
  const confirm = (target: FeedTemplateTarget) =>
    openModal(
      <ConfirmationModal
        title={`Switch to ${target} template now?`}
        actionButtonLabel='Yes'
        actionButtonClose='Cancel'
        handleConfirm={() => {
          // Dispatch once, then expose progress even if the start response never arrives.
          void start(target).catch(() => undefined);
        }}
        closeModal={closeModal}
      />
    );
  return (
    <Stack spacing={2}>
      <Stack sx={styles.row}>
        <Stack spacing={0.75}>
          <Typography variant='h6' sx={styles.title}>
            Switch Feed Template
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Follow each backend stage and review its result before confirming feed readiness.
          </Typography>
        </Stack>
        <Stack sx={styles.selector}>
          {(['main', 'backup'] as const).map((target) => (
            <Button
              key={target}
              aria-label={targetLabel(target)}
              aria-pressed={activeTemplate === target}
              variant={activeTemplate === target ? 'contained' : 'text'}
              disabled={disabled}
              onClick={() => {
                if (activeTemplate !== target) confirm(target);
              }}
              sx={styles.templateOption}
            >
              {targetLabel(target)}{' '}
              {activeTemplate === target && (
                <Box component='span' sx={styles.activeBadge}>
                  Active
                </Box>
              )}
            </Button>
          ))}
        </Stack>
      </Stack>
      {isLoading && <LinearProgress aria-label='Loading switch status' />}
      {startError && <Alert severity='warning'>{startError}</Alert>}
      {statusUnavailable && (
        <Alert severity='warning'>
          <AlertTitle>Status unavailable</AlertTitle>The backend operation may still be running. Results below may be
          stale. Refresh status before taking another action.
        </Alert>
      )}
      {activeTemplate === null && !isLoading && (
        <Alert severity='warning'>
          Active template is unconfirmed. A later failure may occur after the provider has switched.
        </Alert>
      )}
      {operation && (
        <Stack spacing={1.5} role='region' aria-label='Feed template switch progress'>
          <Typography variant='subtitle1' sx={styles.title} role='status'>
            {OPERATION_LABELS[operation.status]}
          </Typography>
          <Typography variant='body2'>Origin: {ORIGIN_LABELS[operation.origin ?? 'manual']}</Typography>
          <Typography variant='body2'>
            Requested switch: {targetLabel(operation.sourceTemplate)} → {targetLabel(operation.targetTemplate)}
          </Typography>
          <Typography variant='caption' sx={styles.wrap}>
            Operation: {operation.id}
          </Typography>
          {operation.stages.length > 0 && (
            <Stack component='ol' spacing={1} sx={styles.stageList}>
              {operation.stages.map((stage, index) => (
                <Stack component='li' key={stage.id} spacing={0.75} sx={styles.stage}>
                  <Stack sx={styles.row}>
                    <Typography variant='body2' sx={styles.title}>
                      {index + 1}. {stage.label}
                    </Typography>
                    <Chip size='small' label={STATUS_LABELS[stage.status]} color={STATUS_COLORS[stage.status]} />
                  </Stack>
                  {stage.message && (
                    <Typography variant='body2' color='text.secondary' sx={styles.wrap}>
                      {stage.message}
                    </Typography>
                  )}
                  {stage.startedAt && (
                    <Typography variant='caption' color='text.secondary'>
                      Started: {dayjs(stage.startedAt).format(`${DATE_TIME_FORMAT}:ss`)}
                      {stage.finishedAt && ` · Finished: ${dayjs(stage.finishedAt).format(`${DATE_TIME_FORMAT}:ss`)}`}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
          {operation.message && (
            <Alert severity={operation.status === 'failed' || operation.status === 'interrupted' ? 'error' : 'info'}>
              {operation.message}
            </Alert>
          )}
        </Stack>
      )}
      {controls.canRelease && <ReleaseTemplateSwitch key={operation?.id} {...controls} />}
      {operation?.reconciliation && (
        <Alert severity='info'>
          Lock released by {operation.reconciliation.releasedByName ?? operation.reconciliation.releasedBy}:{' '}
          {operation.reconciliation.reason}
        </Alert>
      )}
      <Alert severity='info'>
        <AlertTitle>Feed readiness</AlertTitle>
        Feed Running: {feedRunningLabel(feedRunning)}. Snapshot request success does not confirm snapshot processing or
        queue acknowledgement. Check RabbitMQ to confirm the fixtures queue has acknowledged all messages. If the app is
        still in maintenance mode after that, turn Feed Running back on using the feed controls below or in the Flags
        tab.
      </Alert>
      <Button variant='outlined' onClick={refresh} sx={styles.refresh}>
        Refresh status
      </Button>
    </Stack>
  );
};

const FeedTemplateSwitch = () => <FeedTemplateSwitchView controls={useFeedTemplateSwitch()} />;

export default FeedTemplateSwitch;
